<template>
    <div class="flexcol-fill overflow-hidden">
        <div class="flexcol-fix">
            <f-api ref="api"/>
            <v-alert v-if="!calendarService" type="warning">
                Noch kein Kalenderdienst eingerichtet.
            </v-alert>
            <v-alert v-if="error !== null" type="error" class="mt-2">
                {{ error }}
            </v-alert>
            <div v-if="(loadingEvents) && firstLoad" class="text-center">
                <v-progress indeterminate/>
            </div>        
            <kc-calendar-select v-if="calendarId === null" v-show="calendarId === null && !loadingEvents" v-model="calendarId"/>                
            <v-tabs v-if="calendarId !== null && error === null && !firstLoad" v-model="selectedTab">
                <v-tab value="aktuell">Aktuell <v-chip v-if="currentEvents.length > 0">{{ currentEvents.length }}</v-chip></v-tab>
                <v-tab value="woche">Woche</v-tab>
                <v-tab value="monat">Monat</v-tab>
            </v-tabs>
        </div>
        <div v-if="calendarId !== null" class="flexcol-fill overflow-hidden">
            <v-window v-model="selectedTab" class="flexcol-fill overflow-hidden">
                <v-window-item value="aktuell" class="flexcol-fill overflow-auto">
                    <h2 class="border-light border-b-sm border-opacity-100 text-light mt-5 pb-2">Heute</h2>
                    <v-container fluid class="text-light ma-0 pa-0">
                        <v-row v-if="heuteSport === true">
                            <v-col class="bg-warning pa-2">
                                <v-icon icon="mdi-basketball"/> Sportsachen Emil
                            </v-col>
                        </v-row>
                        <v-row v-if="todayEvents.length == 0">
                            <v-col class="text-light-darken-3">
                                <v-icon icon="mdi-information-outline" class="mr-2"/>Keine Termine heute
                            </v-col>
                        </v-row>                    
                        <v-row v-for="event in todayEvents" v-bind:key="event.id" density="compact" align="center">
                            <v-col v-if="event.start.dateTime === null" class="text-light-darken-3">
                                <v-icon icon="mdi-calendar-outline"/>
                                {{ event.summary }}
                            </v-col>
                            <v-col v-if="event.start.dateTime !== null" cols="auto" class="font-weight-bold">
                                <v-icon icon="mdi-clock-outline"/> {{ $dayjs(event.start.dateTime).format('HH:mm') }}
                            </v-col>
                            <v-col v-if="event.start.dateTime !== null" class="pt-1 pl-3">
                                {{ event.summary }}
                            </v-col>
                        </v-row>
                    </v-container>                
                    <h2 class="border-light border-b-sm border-opacity-100 text-light mt-5 pb-2">Morgen</h2>
                    <v-container fluid class="text-light ma-0 pa-0">
                        <v-row v-if="tomorrowEvents.length == 0">
                            <v-col class="text-light-darken-3">
                                <v-icon icon="mdi-information-outline" class="mr-2"/>Keine Termine heute
                            </v-col>
                        </v-row>                    
                        <v-row v-for="event in tomorrowEvents" v-bind:key="event.id" align="center" density="compact">
                            <v-col v-if="event.start.dateTime !== undefined" cols="auto" class="font-weight-bold">
                                <v-icon icon="mdi-clock-outline"/> {{ $dayjs(event.start.dateTime).format('HH:mm') }}
                            </v-col>
                            <v-col class="pl-3 pt-1">
                                {{ event.summary }}
                            </v-col>
                        </v-row>
                    </v-container>
                </v-window-item>
                <v-window-item value="woche" class="flexcol-fill overflow-auto">
                    <v-container fluid class="text-light">
                        <v-row v-for="day in weekDays" v-bind:key="day.date" align="start" :class="dayBg(day) + ' text-light border-light border-b-sm border-opacity-100 mt-0 pt-2 pb-2'">
                            <v-col cols="auto text-l pa-3" style="max-width: 2rem;">
                                {{ $dayjs(day.date).format('dd') }}
                            </v-col>
                            <v-col class="pa-3">
                                <v-container fluid class="pa-0 ma-0">
                                    <v-row v-if="day.events.length == 0">
                                        <v-col>
                                            <span class="text-light-darken-3">Keine Termine</span>
                                        </v-col>
                                    </v-row>
                                    <v-row v-for="event in day.events" v-bind:key="event.id" align="start" density="compact">
                                        <v-col v-if="event.start.dateTime === null" class="text-light-darken-3">
                                            {{ event.summary }}
                                        </v-col>
                                        <v-col v-if="event.start.dateTime !== null" cols="auto">
                                            {{ $dayjs(event.start.dateTime).format('HH:mm') }}
                                        </v-col>
                                        <v-col v-if="event.start.dateTime !== null" class="pl-3">
                                            {{ event.summary }}
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </v-col>                            
                        </v-row>
                    </v-container>
                </v-window-item>
                <v-window-item value="monat" class="flexcol-fill overflow-auto">
                    <v-container fluid class="pa-0 mt-3">
                        <v-row align="center" class="pb-1 border-light border-b-sm border-opacity-100">
                            <v-col cols="auto">
                                <v-btn icon="mdi-arrow-left" @click="prevMonth"/>
                            </v-col>
                            <v-col class="text-center text-light">
                                <span class="font-weight-bold" style="font-size: 1.5rem;">{{ monthDate.format('MMMM YYYY') }}</span>
                            </v-col>
                            <v-col cols="auto">
                                <v-btn icon="mdi-arrow-right" @click="nextMonth"/>
                            </v-col>                        
                        </v-row>
                        <v-row align="center" class="ma-0 text-center text-light text-sm font-weight-bold border-light border-b-sm border-opacity-100">
                            <v-col>Mo</v-col>
                            <v-col>Di</v-col>
                            <v-col>Mi</v-col>
                            <v-col>Do</v-col>
                            <v-col>Fr</v-col>
                            <v-col class="pt-4 pb-4 bg-surface-lighten-1">Sa</v-col>
                            <v-col class="pt-4 pb-4 bg-surface-lighten-1">So</v-col>
                        </v-row>
                        <v-row v-for="week in monthWeeks" v-bind:key="week.date" class="ma-0 text-center">
                            <v-col v-for="day in week.days" v-bind:key="day.date" :class="'pt-4 pb-4 ' + dayBg(day)">
                                <div :class="day.events.length > 0 ?'bg-primary border-light border-sm border-opacity-100' : ''" role="button" @click="showEventsDialog(day)" style="min-height: 3.5rem;">
                                    <div class="text">{{ day.day }}</div>
                                    <div v-if="day.events.length > 0" class="text-sm">{{ day.events.length }}</div>
                                </div>                                                         
                            </v-col>
                        </v-row>             
                    </v-container>
                </v-window-item>
            </v-window>
            <v-dialog v-if="eventDialog.show === true" v-model="eventDialog.show" attach scrim opacity="0.6" max-width="50rem">
                <v-btn
                    icon="mdi-close"
                    variant="text"
                    style="position:absolute; top:0.2rem; right: 0.5rem; z-index:1"
                    @click="eventDialog.show = false"/>
                <v-card>
                    <v-card-title class="bg-primary text-white pa-3">
                        <span>Termine am  {{ $dayjs(eventDialog.day.dateTime).format('dddd, DD. MMMM YYYY') }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-container fluid class="text-light">
                            <v-row align="center" v-for="event in eventDialog.day.events" v-bind:key="event.id">
                                <v-col cols="auto" v-if="event.start.dateTime !== null" >
                                    <v-icon icon="mdi-clock-outline"/> {{ $dayjs(event.start.dateTime).format('HH:mm') }}
                                </v-col>
                                <v-col cols="auto" v-if="event.start.dateTime === null">
                                    <v-icon icon="mdi-calendar-outline"/>
                                </v-col>
                                <v-col>
                                    {{ event.summary }}
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>      
                </v-card>
            </v-dialog>
        </div>
    </div>
</template>
<script>
import kcCalendarSelect from './calendar-select.vue';
export default {
    name: 'kc-calendar',
    components: {
        kcCalendarSelect
    },
    data() {
        return {    
            error: null,
            availableCalendars: [],
            calendarId: null,
            firstLoad: true,
            loadingEvents: false,
            lastRefresh: null,
            events: [],
            monthDate: null,
            monthWeeks: null,
            eventDialog: {
                show: false,
                date: null
            },
            selectedTab: 'aktuell'
        }
    },
    computed: {
        account() {
            const vm = this;
            return vm.$store.state.account;
        },
        calendarService() {
            const vm = this;
            if (vm.account === null) return null;
            if (!vm.account.connectedServices) return null;
            let service = vm.account.connectedServices.find(x => x.type == 'CalendarService');
            if (!service) return null;
            return service;
        },        
        currentEvents() {
            const vm = this;
            return vm.todayEvents.concat(vm.tomorrowEvents);
        },
        todayEvents() {
            const vm = this;
            var events = vm.getEventsOfDate(vm.$dayjs());
            return events;
        },        
        tomorrowEvents() {
            const vm = this;
            var events = vm.getEventsOfDate(vm.$dayjs().add(1, 'days'));
            return events;
        },
        weekDays() {
            const vm = this;            
            var days = [];
            var date = vm.$dayjs();
            for(var i = 1; i < 8; i++) {
                var day = {
                    date: date.format('YYYY-MM-DD'),
                    events: vm.getEventsOfDate(date)
                };
                days.push(day);
                date = date.add(1, 'days');
            }
            return days;
        },
        stundenplanWoche() {
            const vm = this;
            const week = vm.$dayjs().isoWeek();
            return week % 2 === 0 ? "B" : "A";
        },        
        heuteSport() {
            const vm = this;
            let stundenplan = vm.$store.state.stundenplan;
            let tag = stundenplan.tage.find(x => x.tag === vm.$dayjs().format('dd'));
            if (tag) {
                let sport = tag.stunden.filter(s => s.fach == 'Sport' && (!s.woche || s.woche === vm.stundenplanWoche));
                return (sport.length > 0);
            }
            return false;
        }
    },
    watch: {
        calendarId(v) {
            const vm = this;
            localStorage["calendarId"] = v;
            if (v !== null) {
                vm.init();
            }
        } 
    },
    mounted() {
        const vm = this;
        vm.init();
    },
    methods: {         
        async init() {
            const vm = this;
            vm.monthDate = vm.$dayjs(vm.$dayjs().format('YYYY-MM-') + '01');
            let calId = localStorage["calendarId"];
            if (calId) {
                vm.calendarId = calId
                await vm.loadEvents();
                vm.startRefresh();
            } else {
                vm.calendarId = null;
            }
        },
        async startRefresh() {
            const vm = this;
            if (vm.refreshInterval !== null) {
                vm.stopRefresh();
            }
            vm.refreshInterval = setInterval(async function() {
                if (!vm.loadingEvents) {
                    await vm.loadEvents();
                    vm.lastRefresh = vm.$dayjs();
                }
            }, 30000);
        },
        stopRefresh() {
            const vm = this;
            if (vm.refreshInterval) {
                clearInterval(vm.refreshInterval);
            }
        },
        async loadEvents() {
            const vm = this;
            vm.error = null;        
            vm.loadingEvents = true;
            var startDate = vm.$dayjs().format('YYYY-MM-DD');
            var endDate = vm.$dayjs().add(600, 'days').format('YYYY-MM-DD');
            try {
                vm.events = await vm.$refs.api.getAsync('calendar/' + vm.calendarId + '/events?from=' + startDate + '&to=' + endDate);
                vm.loadingEvents = false;
                vm.firstLoad = false;
                vm.updateMonthWeeks();
            } catch(err) {
                vm.loadingEvents = false;
                vm.firstLoad = false;
                vm.error = err;
            }
        },
        getEventsOfDate(date) {
            const vm = this;
            if (!vm.events) {
                return []
            }
            return vm.events.filter(x => (x.start.date !== undefined && x.start.date === date.format('YYYY-MM-DD'))
                                            || (x.start.dateTime !== null && x.start.dateTime.startsWith(date.format('YYYY-MM-DD'))));     
        },
        dayBg(day) {
            const vm = this;
            var weekday = vm.$dayjs(day.date).format('dd');
            if (weekday === "Sa" || weekday === "So") {
                return "bg-surface-lighten-1";
            }
            return "";
        },
        updateMonthWeeks() {
            var vm = this;
            vm.monthWeeks = [];
            var startDate = vm.$dayjs(vm.monthDate.format('YYYY-MM-DD'));
            if (startDate.format('dd') === "Di") startDate = startDate.add(-1, 'days');
            if (startDate.format('dd') === "Mi") startDate = startDate.add(-2, 'days');
            if (startDate.format('dd') === "Do") startDate = startDate.add(-3, 'days');
            if (startDate.format('dd') === "Fr") startDate = startDate.add(-4, 'days');
            if (startDate.format('dd') === "Sa") startDate = startDate.add(-5, 'days');
            if (startDate.format('dd') === "So") startDate = startDate.add(-6, 'days');
            var date = startDate;
            var week = {
                days: []
            };
            var exitWhile = false;
            while(!exitWhile)
            {
                var day = {
                    date: date.format('YYYY-MM-DD'),
                    day: date.format('D'),
                    today: (date.format('YYYY-MM-DD') === vm.$dayjs().format('YYYY-MM-DD')),
                    otherMonth: (date.format('MMM') !== vm.monthDate.format('MM')),
                    events: vm.getEventsOfDate(date)
                }
                week.days.push(day);
                if (date.format('dd') === "So") {
                    vm.monthWeeks.push(week);
                    week = {
                        days: []
                    };
                    date = date.add(1, 'days');                    
                    if (date.format('MM') !== vm.monthDate.format('MM')) exitWhile = true;
                } else {
                    date = date.add(1, 'days');
                }
            }
        },        
        prevMonth() {
            const vm = this;
            vm.monthDate = vm.monthDate.add(-1, 'month');
            vm.updateMonthWeeks();
        },
        nextMonth() {
            const vm = this;
            vm.monthDate = vm.monthDate.add(1, 'month');
            vm.updateMonthWeeks();
        },        
        showEventsDialog(day) {
            const vm = this;
            vm.eventDialog.show = true;
            vm.eventDialog.day = day;
        }
    }
}
</script>
<style lang="sass" scoped>
</style>